import React, { useState, useCallback, useEffect } from 'react';
import { useApi } from '@/shared/hooks/useApi';
import targetGuestService from '@/services/api/target-guests';
import type { TargetGuest } from '@/types/models/schema';
import type { Guest } from '@/types/models/schema';
import type { FilterOptions, ApiResponse } from '@/types/api';
import { AxiosInstance } from 'axios';

/**
 * Simplified useTargetGuests Hook
 * 
 * Provides data and operations for managing potential podcast guests
 */
export function useTargetGuests(initialFilters?: FilterOptions) {
  const [filters, setFilters] = useState<FilterOptions>(initialFilters || {});
  
  // Use the updated useApi hook pattern with correct signature for raw data
  const {
    data: targetGuests,
    isLoading,
    error,
    refetch,
    retry
  } = useApi<TargetGuest[]>(
    useCallback(async (client: AxiosInstance) => {
      return targetGuestService.getAll(filters);
    }, [filters])
  );
  
  const fetchTargetGuests = useCallback(async (filterOptions?: FilterOptions) => {
    if (filterOptions) {
      setFilters(filterOptions);
    }
    return refetch();
  }, [refetch]);
  
  const getTargetGuestById = useCallback(async (id: string): Promise<ApiResponse<TargetGuest>> => {
    const rawResponse = await targetGuestService.getById(id);
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<TargetGuest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to fetch target guest with ID ${id}`) : null
    };
    
    return response;
  }, []);
  
  const createTargetGuest = useCallback(async (data: Omit<TargetGuest, 'id' | 'created_at'>): Promise<ApiResponse<TargetGuest>> => {
    const rawResponse = await targetGuestService.create(data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<TargetGuest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error('Failed to create target guest') : null
    };
    
    return response;
  }, [refetch]);
  
  const updateTargetGuest = useCallback(async (id: string, data: Partial<TargetGuest>): Promise<ApiResponse<TargetGuest>> => {
    const rawResponse = await targetGuestService.update(id, data);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<TargetGuest> = {
      data: rawResponse,
      error: rawResponse === null ? new Error(`Failed to update target guest with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const deleteTargetGuest = useCallback(async (id: string): Promise<ApiResponse<boolean>> => {
    const rawResponse = await targetGuestService.delete(id);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to delete target guest with ID ${id}`) : null
    };
    
    return response;
  }, [refetch]);
  
  const convertToGuest = useCallback(async (id: string, additionalData?: Partial<Guest>): Promise<ApiResponse<boolean>> => {
    const rawResponse = await targetGuestService.convertToGuest(id, additionalData);
    refetch();
    
    // Wrap raw response in ApiResponse
    const response: ApiResponse<boolean> = {
      data: rawResponse,
      error: rawResponse === false ? new Error(`Failed to convert target guest with ID ${id} to guest`) : null
    };
    
    return response;
  }, [refetch]);
  
  // Load target guests on mount and when filters change
  useEffect(() => {
    fetchTargetGuests();
  }, [fetchTargetGuests]);
  
  return {
    targetGuests,
    isLoading,
    error,
    filters,
    setFilters,
    fetchTargetGuests,
    getTargetGuestById,
    createTargetGuest,
    updateTargetGuest,
    deleteTargetGuest,
    convertToGuest,
    refetchTargetGuests: refetch,
    retryFetch: retry
  };
}

export default useTargetGuests;
